import React, { useEffect, useState } from 'react';
import { FiBriefcase, FiX } from 'react-icons/fi';
import {
  PALETTE,
  StationHeader,
} from '../station/stationTheme';
import {
  CorpDetailView,
  CorpListView,
  useCorporationData,
} from '../corp';

const FOUND_HINT =
  'Founding a corporation is a legal act and must be done in person at a station Corporation Office. Once founded you can manage it from here.';

interface Props {
  /** Optional controlled-open prop — for hotkey wiring from a parent. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CorporationHUD({ open: controlledOpen, onOpenChange }: Props = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };

  // Animation state — mirrors ShipInventoryModal: `visible` lags `open` by one
  // frame so the slide-in transition can play. The drawer is always mounted in
  // the DOM while `open` is true; pointer events are gated on `visible`.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    return undefined;
  }, [open]);

  const data = useCorporationData({ autoFetch: true });

  // 'O' (Organization / cOrp) toggles the panel from anywhere in space
  // (ignored while typing). C is reserved for chat.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.repeat) return;
      if (e.key.toLowerCase() === 'o') {
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Note: ESC intentionally does NOT close this drawer — only the 'O' hotkey
  // (or clicking the briefcase / backdrop) toggles it. ESC is left untouched
  // so the broader game UI can keep using it for its own things.

  // Refresh the list whenever the user opens the panel so the badge / view
  // stay in sync with server-side activity (applications, distributions).
  useEffect(() => {
    if (open) data.refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pendingAttention = (data.corps ?? []).reduce((s, c) => s + (c.pendingApplicationsCount || 0), 0);

  return (
    <>
      <button
        style={btnStyle}
        onClick={() => setOpen(!open)}
        title={pendingAttention > 0 ? `Corporation — ${pendingAttention} pending request${pendingAttention === 1 ? '' : 's'} [O]` : 'Corporation [O]'}
      >
        <FiBriefcase size={16} />
        {/* Orange "!" badge only when there's something needing attention
            (pending membership applications waiting on a manager). The plain
            corp-count badge was misleading — users read it as "you have a
            notification" when really it just meant "you belong to N corps". */}
        {pendingAttention > 0 && (
          <span style={attentionBadgeStyle}>{pendingAttention}</span>
        )}
      </button>

      <div
        style={drawerOverlayStyle(open, visible)}
        onClick={() => {
          if (data.detail) data.closeDetail();
          else setOpen(false);
        }}
      >
        <div style={drawerStyle(visible)} onClick={(e) => e.stopPropagation()}>
          {data.detail ? (
            <>
              <StationHeader
                onBack={() => data.closeDetail()}
                title={`${(data.detail.name || '').toUpperCase()} [${data.detail.tag}]`}
                hint="[O] Close"
              />
              <CorpDetailView
                detail={data.detail}
                busy={data.busy}
                error={data.error}
                onWithdraw={data.withdraw}
                onSetDistribution={data.setDistribution}
                onStartVote={data.startVote}
                onCastVote={data.castVote}
                onSetPermission={data.setPermission}
                onKickMember={data.kickMember}
                onRespondApplication={data.respondApplication}
              />
            </>
          ) : (
            <>
              <DrawerHeader title="CORPORATION" onClose={() => setOpen(false)} />
              <ScrollBody>
                <CorpListView
                  corps={data.corps}
                  busy={data.busy}
                  error={data.error}
                  applyMsg={data.applyMsg}
                  onOpenCorp={data.openCorp}
                  onApply={(query) => data.apply({ corp: query })}
                  onLeave={() => data.leave()}
                  foundUnavailableHint={FOUND_HINT}
                />
              </ScrollBody>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function DrawerHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={drawerHeaderStyle}>
      <span style={drawerTitleStyle}>{title}</span>
      <button style={closeBtnStyle} onClick={onClose} title="Close [O]">
        <FiX size={14} />
      </button>
    </div>
  );
}

function ScrollBody({ children }: { children: React.ReactNode }) {
  return (
    <div style={scrollBodyStyle}>
      <div style={scrollInnerStyle}>{children}</div>
    </div>
  );
}

export default CorporationHUD;

// ===== STYLES =====

const btnStyle: React.CSSProperties = {
  position: 'absolute',
  // Anchored next to the bottom-center HUD bars (HULL/HEAT/BOOST/POWER/SHIELD).
  bottom: 20,
  left: 'calc(50% + 168px)',
  zIndex: 50,
  pointerEvents: 'auto',
  width: 34,
  height: 34,
  borderRadius: 6,
  background: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  color: PALETTE.TEAL,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'JetBrains Mono', monospace",
};

const attentionBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -6,
  right: -6,
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  borderRadius: 8,
  color: PALETTE.DARK,
  fontSize: 10,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: PALETTE.ORANGE,
};

// Right-side slide-in drawer, matching ShipInventoryModal exactly.
const drawerOverlayStyle = (mounted: boolean, visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: visible ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
  zIndex: 100,
  pointerEvents: mounted ? 'auto' : 'none',
  transition: 'background-color 0.3s ease',
});

const drawerStyle = (visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '704px',
  maxWidth: '95vw',
  height: '100%',
  backgroundColor: 'rgba(15, 27, 38, 0.92)',
  borderLeft: `2px solid ${PALETTE.TEAL}`,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  boxShadow: `-4px 0 40px ${PALETTE.TEAL}22`,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
  transform: visible ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.3s ease',
});

const drawerHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const drawerTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: PALETTE.LIGHT,
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: PALETTE.DIM,
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
};

const scrollBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
};

const scrollInnerStyle: React.CSSProperties = {
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
};
